import {
    InsightGetFundsForHoldingRequestLeverageEnum,
    InsightGetTopFundsRequestInverseEnum,
} from '@orbis-systems/orbis-api-clients/insight';
import { addHours, formatDistance, isValid, parseISO } from 'date-fns';
import { format, fromZonedTime, toZonedTime } from 'date-fns-tz';
import * as locales from 'date-fns/locale';
import { camelCase, isArray, startCase } from 'lodash-es';
import { v4 } from 'uuid';
import { API_DATE_YEAR_FIRST, MARKET_TIMEZONE } from '../constants/datePatterns';
import { AgreementCode, GlobalGroupCode, IndustrySector, TimeRange } from '../constants/lookups';
import { CryptoPair } from '../types';
import { divide } from './numberUtils';

export function titleCase(value: string): string {
    return startCase(camelCase(value));
}

export function formatDate(
    input?: string | number | Date,
    {
        short = false,
        pattern: initialPattern,
        withTime = false,
        distance = false,
        timeZone,
        localeCode = 'en-US',
        withTimeZone = true,
        placeholder = '-',
    }: {
        short?: boolean;
        pattern?: string;
        withTime?: boolean;
        distance?: boolean;
        timeZone?: string;
        localeCode?: string;
        withTimeZone?: boolean;
        placeholder?: string;
    } = {},
): string {
    if (!input) {
        return placeholder;
    }

    let pattern = initialPattern || `MMM dd, yyyy${withTime ? ' HH:mm:ss' : ''}${withTimeZone && timeZone ? ' z' : ''}`;
    if (short) {
        pattern = pattern.replace(', yyyy', '');
    }

    let date: Date;
    let timeZoneData = '';

    if (typeof input === 'string') {
        date = parseISO(input);

        // Not an ISO date
        if (!isValid(date)) {
            const match = input.match(/[a-zA-Z]{2,}$/);
            timeZoneData = match?.[0] ?? '';
            input = input.replace(timeZoneData, '').trim();
        } else {
            const match = input.match(/Z$|[+-]\d{2}:?\d{2}$/);
            if (match && !timeZone) {
                timeZoneData = match[0];
                input = input.replace(timeZoneData, '').trim();

                if (timeZoneData === 'Z' || timeZoneData === '+0000') {
                    timeZoneData = 'UTC';
                } else {
                    timeZoneData = `UTC${timeZoneData}`;
                }
            }
        }
    }

    date = new Date(input);

    if (!isValid(date)) {
        return placeholder;
    }

    // Return relative time if distance flag is enabled
    if (distance) {
        return formatDistance(date, new Date(), { addSuffix: true });
    }

    // Format date (with optional timezone conversion)
    const formattedDate = format(timeZone ? toZonedTime(date, timeZone) : date, pattern, {
        timeZone,
        locale: localeCode ? getLocaleByCode(localeCode) : undefined,
    });

    if (timeZoneData && withTimeZone) {
        return `${formattedDate} ${timeZoneData}`;
    }

    return formattedDate;
}

export function getLocaleByCode(localeCode: string) {
    const localeList = Object.values(locales);
    return localeList.find((item) => item.code === localeCode) || locales.enUS;
}

export function parseOptionSymbol(optionSymbol: string) {
    try {
        // the length of an option symbol has 21 chars
        if (optionSymbol.length !== 21 || (optionSymbol[12] !== 'C' && optionSymbol[12] !== 'P')) {
            return null;
        }

        const symbol = optionSymbol.substr(0, 6).trim();
        const dateString = optionSymbol.substr(6, 6);

        const date = fromZonedTime(
            addHours(
                new Date(
                    Number('20' + dateString.substr(0, 2)),
                    Number(dateString.substr(2, 2)) - 1,
                    Number(dateString.substr(4, 2)),
                ),
                16,
            ),
            MARKET_TIMEZONE,
        );

        const expiration = formatDate(date, { withTimeZone: true, timeZone: MARKET_TIMEZONE, withTime: true });
        const call = optionSymbol[12] === 'C';
        const strike = divide(Number(optionSymbol.substr(13)), 1000);

        return {
            symbol,
            expiration,
            call,
            strike,
        };
    } catch {
        return null;
    }
}

export const getSectorByIndustryCode = (code: GlobalGroupCode) => {
    switch (code) {
        case GlobalGroupCode.BasicMaterial:
            return IndustrySector.BasicMaterial;
        case GlobalGroupCode.CommunicationServices:
            return IndustrySector.CommunicationServices;
        case GlobalGroupCode.ConsumerCyclical:
            return IndustrySector.ConsumerCyclical;
        case GlobalGroupCode.ConsumerDefensive:
            return IndustrySector.ConsumerDefensive;
        case GlobalGroupCode.Energy:
            return IndustrySector.Energy;
        case GlobalGroupCode.FinancialServices:
            return IndustrySector.FinancialServices;
        case GlobalGroupCode.Healthcare:
            return IndustrySector.Healthcare;
        case GlobalGroupCode.Industrials:
            return IndustrySector.Industrials;
        case GlobalGroupCode.RealEstate:
            return IndustrySector.RealEstate;
        case GlobalGroupCode.Technology:
            return IndustrySector.Technology;
        case GlobalGroupCode.Utilities:
            return IndustrySector.Utilities;

        default:
            return IndustrySector.Energy;
    }
};

export function parseCryptoSymbol(symbol: string | string[], separator = '/'): CryptoPair {
    let base = '';
    let quote = '';

    if (isArray(symbol)) {
        base = symbol[0] || '';
        quote = symbol[1] || '';
    } else {
        const symbols = symbol.toUpperCase().split(/[_\-/]/);
        base = symbols[0] || '';
        quote = symbols[1] || '';
    }

    return {
        base,
        quote,
        symbol: quote ? `${base}${separator}${quote}` : base,
        symbolNoSeparator: (base || '') + (quote || ''),
    };
}

export const getLeverageType = (id: InsightGetFundsForHoldingRequestLeverageEnum | string) => {
    switch (id) {
        case InsightGetFundsForHoldingRequestLeverageEnum.ANY:
            return 'Any Leverage';
        case InsightGetFundsForHoldingRequestLeverageEnum.LEVERAGE_I:
            return 'Leverage x1';
        case InsightGetFundsForHoldingRequestLeverageEnum.LEVERAGE_II:
            return 'Leverage x2';
        case InsightGetFundsForHoldingRequestLeverageEnum.LEVERAGE_III:
            return 'Leverage x3';
        case InsightGetFundsForHoldingRequestLeverageEnum.LEVERAGE_II_AND_III:
            return 'Leverage x2 and x3';
        default:
            return id;
    }
};

export const getInverseType = (id: InsightGetTopFundsRequestInverseEnum | string) => {
    switch (id) {
        case InsightGetTopFundsRequestInverseEnum.SHORT:
            return 'Short';
        case InsightGetTopFundsRequestInverseEnum.LONG:
            return 'Long';
        case InsightGetTopFundsRequestInverseEnum.ANY:
            return 'Any';

        default:
            return id;
    }
};

export const getAgreementName = (code: AgreementCode) => {
    switch (code) {
        case AgreementCode.NyseAmex:
            return 'NYSE/AMEX';
        case AgreementCode.DowJones:
            return 'Dow Jones Index';
        case AgreementCode.Options:
            return 'Options';
        case AgreementCode.Nasdaq:
            return 'NASDAQ';
        case AgreementCode.SP500:
            return 'S&P 500';
        case AgreementCode.Canada:
            return 'Canada';
        case AgreementCode.CboeGlobal:
            return 'CBOE Global';
        default:
            return code;
    }
};

export const formatLeverage = (leverage: number) => {
    switch (leverage) {
        case 1:
            return 'No Leverage';
        case 2:
            return 'Leverage x2';
        case 3:
            return 'Leverage x3';
        default:
            return 'Any Leverage';
    }
};

export const getUuid = () => v4();

export const getBondStartMonthRange = (range: TimeRange) => {
    switch (range) {
        case TimeRange.OneMonth:
            return 1;
        case TimeRange.ThreeMonth:
            return 3;
        case TimeRange.SixMonth:
            return 6;
        case TimeRange.SevenMonth:
            return 7;
        case TimeRange.NineMonth:
            return 9;
        case TimeRange.OneYear:
            return 12;
        default:
            return 0;
    }
};

export const getBondStartMonth = (range: TimeRange) => {
    const now = new Date();
    if (range === TimeRange.OneDay) {
        return formatDate(now.setDate(now.getDate() - 1), {
            pattern: API_DATE_YEAR_FIRST,
        });
    }

    const rangeId = getBondStartMonthRange(range) || 1;
    now.setMonth(now.getMonth() - rangeId);
    return formatDate(now, {
        pattern: API_DATE_YEAR_FIRST,
    });
};

export const generatePassword = ({
    numberOfCapitalLetters,
    numbers,
    numberOfLowerCaseLetters,
}: {
    numberOfCapitalLetters: number;
    numbers: number;
    numberOfLowerCaseLetters: number;
}): string => {
    const chars = ['ABCDEFGHIJKLMNOPQRSTUVWXYZ', '0123456789', 'abcdefghijklmnopqrstuvwxyz'];

    const charsFilling = (len: number, i: number) => {
        return Array(len)
            .fill(chars[i])
            .map(function (x) {
                return x[Math.floor(Math.random() * x.length)];
            })
            .join('');
    };

    const sortPasswordChars = () => 0.5 - Math.random();

    return [numberOfCapitalLetters, numbers, numberOfLowerCaseLetters]
        .map(charsFilling)
        .concat()
        .join('')
        .split('')
        .sort(sortPasswordChars)
        .join('');
};
